import {FormElement} from "./form-element.js";

/**
 * Represents a section element within a form, extending the functionality of a base FormElement.
 * Used to manage a specific section of a form, including rendering, style management,
 * and handling the form data for the section.
 */
export class SectionElement extends FormElement {

    fields;
    componentTag = "section";

    /**
     * Constructs a new instance of the class.
     *
     * @param {Object} formData - The data used to populate the form.
     * @param {HTMLElement} parentElement - The parent DOM element where the component will be appended.
     * @param {Array} collection - A collection of items relevant to the component functionality.
     * @return {void} - Does not return a value.
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
    }

    /**
     * Sets up default event listeners for a given component. This method subscribes
     * to resize events on the associated collection and updates the component's
     * style accordingly.
     *
     * @param {Object} component - The component for which event listeners are being set up.
     * @return {void} Does not return any value.
     */
    defaultListeners(component) {
        super.defaultListeners(component);

        const subscription = this.collection.subscribeToResize(args => {
            this.setComponentStyle(this.domComponent);
        });
        this.subscriptions = [...this.subscriptions ?? [], subscription];
    }

    /**
     * Sets the style for a given component by defining its grid layout.
     *
     * @param {Object} component - The component object to which the style will be applied.
     * @return {void} This method does not return a value.
     */
    setComponentStyle(component) {
        this.defineGrid(component);
    }

    /**
     * Creates a form section component by extending the base component behavior.
     * Assigns a specific class name to the section element for styling and identification.
     *
     * @return {HTMLElement} Returns the modified section element with the assigned class name.
     */
    component() {
        const sectionElement = super.component();
        sectionElement.className = 'form-section';

        return sectionElement;
    }

    /**
     * Prepares and modifies the `formData` object based on the presence of the `field` property.
     * If the `field` property exists and the corresponding key in `formData` is not already defined,
     * it initializes that key with an empty object. It then updates `formData` to reference the value
     * of the specified `field`.
     *
     * @return {void} This method does not return a value; it operates directly on the `formData` property.
     */
    preRender() {
        if (this.field) {
            if (!this.formData[this.field]) {
                this.formData[this.field] = {};
            }
            this.formData = this.formData[this.field];
        }
    }

    /**
     * Handles the value change event and performs necessary operations based on the event parameter.
     *
     * @param {Event} event - The event object that contains details about the value change.
     * @return {boolean} Returns false to indicate the event is handled or to stop further processing.
     */
    valueChange(event) { return false; }

    /**
     * Cleans up resources and subscriptions associated with the instance.
     * Iterates through the subscriptions array, unsubscribes each subscription,
     * and logs any errors encountered during the process without interrupting the flow.
     * Calls the parent class's destroy method afterward to complete the destruction process.
     *
     * @return {void} Does not return a value.
     */
    destroy() {
        for (const subscription of this.subscriptions ?? []) {
            try {
                subscription.unsubscribe();
            } catch (error) {
                /* log but do not block or abort */
                console.error(error);
            }
        }

        super.destroy();
    }

}
